import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { AlertCircle, CalendarDays, BellRing, CheckCircle2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Noutati() {
  const [newsList, setNewsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/noutati`);
        setNewsList(response.data);
      } catch (error) {
        console.error("Eroare la preluarea noutăților:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-3 border-b pb-4">
        <BellRing className="w-8 h-8 text-amber-500" />
        <h1 className="text-3xl font-bold text-slate-800">Noutăți și Informații</h1>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex gap-3 text-slate-700 text-sm mb-6">
        <AlertCircle className="w-5 h-5 flex-shrink-0 text-blue-600 mt-0.5" />
        <p>Această pagină conține principalele noutăți și informații de interes public care intervin în organizarea și activitatea serviciului nostru. Cei interesați pot consulta această secțiune pentru actualizări oficiale și modificări legislative sau procedurale.</p>
      </div>

      {/* MODUL FIX (STATIC): Avertizare Legislativa 1 Iulie */}
      <Card className="shadow-lg border-blue-200 overflow-hidden relative mb-8">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-600"></div>
        <CardHeader className="bg-blue-50/50 pb-4">
          <div className="flex justify-between items-start">
            <CardTitle className="text-xl text-blue-900 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-blue-600" />
              COMUNICAT OFICIAL: Modificare Procedură Testare Teoretică
            </CardTitle>
            <span className="text-xs font-bold bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1 shrink-0">
              <CalendarDays className="w-3 h-3" />
              Aplicabil din 01.07.2026
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4 text-slate-700">
          <p className="font-medium text-slate-900">
            În conformitate cu O.U.G. nr. 7/2026 pentru modificarea O.U.G. nr. 195/2002, s-a creat un cadru eficient de digitalizare și programare on-line în sprijinul cetățenilor.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
            <div className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-slate-900">Program de Testare (SRPCIV Gorj):</strong>
                <p className="text-sm mt-1">Începând cu 1 iulie 2026, testul pentru reducerea perioadei de suspendare se va susține la sediul nostru <strong>exclusiv în zilele de marți și joi, între orele 08:30 - 10:30</strong>.</p>
              </div>
            </div>
            
            <div className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-slate-900">Procedura de Examinare:</strong>
                <ul className="text-sm mt-1 list-disc list-inside space-y-1">
                  <li>La ghișeul de examinare (SRPCIV) vă veți prezenta <strong>doar cu actul de identitate și dovada înlocuitoare</strong> a permisului.</li>
                  <li>Rezultatul examinării este transmis electronic automat către Serviciul Rutier (aplicația "abateri").</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 items-start bg-blue-50 p-3 rounded border border-blue-100">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-blue-900">Atenție - Depunerea Cererii de Reducere:</strong>
                <p className="text-sm mt-1 text-blue-800">
                  <strong>Certificatul fiscal</strong> eliberat de primăria de domiciliu este obligatoriu <strong>doar la Serviciul Rutier</strong>. După promovarea testului la SRPCIV, cererea de reducere a pedepsei împreună cu certificatul fiscal se depun la Serviciul Rutier competent teritorial.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-xl font-bold text-slate-800 border-b pb-2">Arhivă Noutăți Generale</h2>

      {/* MODUL DINAMIC (DIN BAZA DE DATE) */}
      {isLoading ? (
        <div className="text-center py-10 text-slate-500">Se încarcă noutățile...</div>
      ) : newsList.length === 0 ? (
        <div className="text-center py-10 text-slate-500">Momentan nu există noutăți adiționale publicate.</div>
      ) : (
        <Accordion type="single" collapsible className="w-full space-y-4">
          {newsList.map((item) => (
            <AccordionItem key={item.id} value={item.id} className="bg-white border rounded-lg shadow-sm px-2">
              <AccordionTrigger className="hover:no-underline px-4 py-4 group">
                <div className="flex flex-col md:flex-row md:items-center gap-2 text-left w-full pr-4">
                  <div className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 w-fit">
                    <CalendarDays className="w-3 h-3" />
                    {new Date(item.date).toLocaleDateString('ro-RO')}
                  </div>
                  <h3 className="font-semibold text-lg text-blue-900 group-hover:text-blue-600 transition-colors">
                    {item.title}
                  </h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 pt-2 border-t border-slate-100">
                <div className="prose prose-sm md:prose-base max-w-none text-slate-700 whitespace-pre-wrap mt-4">
                  <p className="font-medium text-slate-800 mb-4">{item.summary}</p>
                  {item.content}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}